"use client";

import {
  type ButtonProps,
  TreeView as ChakraTreeView,
  createTreeCollection,
  HStack,
  Icon,
  IconButton,
  type IconProps,
  Separator,
  Text,
  useTreeViewContext,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  LuChevronDown,
  LuChevronRight,
  LuChevronUp,
  LuDot,
  LuSquareMinus,
  LuSquarePlus,
} from "react-icons/lu";
import { RxFrame } from "react-icons/rx";
export interface Node {
  value: string;
  label: string;
  action?: any;
  icon?: React.ReactNode;
  children?: Node[];
  permission?: string | string[];
  /** Stable tutorial locator — rendered as data-tutorial-id on leaf items. */
  tutorialId?: string;
}

export interface TreeViewProps {
  items: Node[];
  title?: string;
  labelProps?: ChakraTreeView.ItemTextProps | ButtonProps;
  iconProps?: IconProps;
  triggerType?: "chevron" | "plus";
  onSelect?: (e: { items: Node; value: string }) => void;
}

const ExpandCollapseAll = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const tree = useTreeViewContext();

  useEffect(() => {
    if (isExpanded) {
      tree.expand();
    } else {
      tree.collapse();
    }
  }, [isExpanded]);

  return (
    <IconButton
      aria-label="expand-collapse-toggle"
      variant="plain"
      size="sm"
      color="secondary.fg"
      onClick={() => {
        setIsExpanded(!isExpanded);
      }}
    >
      {isExpanded ? <LuChevronDown /> : <LuChevronUp />}
    </IconButton>
  );
};
export const TreeView = React.forwardRef<HTMLDivElement, TreeViewProps>(
  function TreeView(props, ref) {
    const { title, items, triggerType = "chevron", onSelect, ...rest } = props;

    const collection = createTreeCollection<Node>({
      nodeToValue: (node) => node.value,
      nodeToString: (node) => node.label,
      rootNode: {
        value: "ROOT",
        label: "ROOT",
        children: items,
      },
    });

    return (
      <ChakraTreeView.Root
        ref={ref}
        collection={collection}
        maxW="sm"
        size="sm"
        defaultExpandedValue={["ROOT"]}
        onSelectionChange={(e) => {
          onSelect?.({
            items: e.selectedNodes[0],
            value: e.selectedValue[0],
          });
        }}
        {...rest}
      >
        {title && (
          <>
            <HStack color={"secondary.fg"}>
              <Text textStyle="heading.card" flexShrink="0">
                {title}
              </Text>
              <Separator flex="1" />
              <ExpandCollapseAll />
            </HStack>
          </>
        )}
        <ChakraTreeView.Tree gap={3}>
          <ChakraTreeView.Node
            // indentGuide={<ChakraTreeView.BranchIndentGuide />}
            render={({ node, nodeState }) =>
              nodeState.isBranch ? (
                <ChakraTreeView.BranchControl>
                  {triggerType === "chevron" && (
                    <ChakraTreeView.BranchTrigger>
                      <ChakraTreeView.BranchIndicator asChild>
                        <LuChevronRight />
                      </ChakraTreeView.BranchIndicator>
                    </ChakraTreeView.BranchTrigger>
                  )}
                  {triggerType === "plus" &&
                    (nodeState.expanded ? <LuSquareMinus /> : <LuSquarePlus />)}
                  <ChakraTreeView.BranchText
                    textStyle="heading.tab"
                    color={"fg"}
                    focusRing={"none"}
                    {...props.labelProps}
                  >
                    {node.label}
                  </ChakraTreeView.BranchText>
                </ChakraTreeView.BranchControl>
              ) : (
                <ChakraTreeView.Item>
                  <Icon as={node.icon || LuDot} {...node.iconProps} />
                  <ChakraTreeView.ItemText
                    textStyle="heading.tab"
                    color={"fg"}
                    focusRing={"none"}
                    {...props.labelProps}
                    {...(node.tutorialId
                      ? { 'data-tutorial-id': node.tutorialId }
                      : {})}
                  >
                    {node.label}
                  </ChakraTreeView.ItemText>
                </ChakraTreeView.Item>
              )
            }
          />
        </ChakraTreeView.Tree>
      </ChakraTreeView.Root>
    );
  }
);

const ChakraTreeViewNodeIcon = (props: {
  icon: Node["icon"];
  iconProps?: IconProps;
}) => {
  switch (props.icon) {
    case "frame":
      return <Icon as={RxFrame} {...props.iconProps} />;
    case "rectangle":
    default:
      return null;
  }
};
